import { FunctionalComponent, h } from '@stencil/core';
import { CheckableFormCompoment, FormComponent, HiddenFormElementSlotProps, IndeterminateFormCompoment } from './';
import { closestElementCrossShadowBoundary, setValidElementAttributes } from './dom';

/** Checks if component has checked property. */
function canBeCheckable(component: FormComponent): component is CheckableFormCompoment {
  return 'checked' in component;
}

/** Checks if component has indeterminate property. */
function canBeIndeterminate(component: FormComponent): component is IndeterminateFormCompoment {
  return 'indeterminate' in component;
}

const onFormResetMap = new WeakMap<HTMLElement, FormComponent['onFormReset']>();
const formComponentSet = new WeakSet<HTMLElement>();

/**
 * Checks if a given component is registered to a different form than the given one.
 * @function
 * @param {HTMLFormElement} form - The form to check against.
 * @param {HTMLElement} formComponentEl - The element to check for.
 * @return {boolean} element registered to a different form.
 */
function hasRegisteredFormComponentParent(form: HTMLFormElement, formComponentEl: HTMLElement): boolean {
  // we use events as a way to test for nested form-associated components across shadow bounds
  const formComponentRegisterEventName = 'internalFormComponentRegister';

  let isRegistered = false;

  form.addEventListener(
    formComponentRegisterEventName,
    (event) => {
      isRegistered = event.composedPath().some((element) => formComponentSet.has(element as HTMLElement));
      event.stopPropagation();
    },
    { once: true }
  );

  formComponentEl.dispatchEvent(
    new CustomEvent(formComponentRegisterEventName, {
      bubbles: true,
      composed: true,
    })
  );

  return isRegistered;
}

/** Reset properties of a form component to their defaults. */
function onFormReset(this: FormComponent): void {
  this.value = this.defaultValue;

  if (canBeCheckable(this)) this.checked = this.defaultChecked;
  if (canBeIndeterminate(this)) this.indeterminate = this.defaultIndeterminate;
}

/** Helper to set up form interactions on connectedCallback. */
export function connectForm(component: FormComponent): void {
  const { host, value } = component;
  const form = closestElementCrossShadowBoundary<HTMLFormElement>(host, 'form');
  const boundOnFormReset = (component.onFormReset || onFormReset).bind(component);

  if (!form || hasRegisteredFormComponentParent(form, host)) return;

  form.addEventListener('reset', boundOnFormReset);
  formComponentSet.add(host);

  component.formEl = form;
  component.defaultValue = value;

  if (canBeCheckable(component)) component.defaultChecked = component.checked;
  if (canBeIndeterminate(component)) component.defaultIndeterminate = component.indeterminate;
}

/** Helper to tear down form interactions on disconnectedCallback. */
export function disconnectForm(component: FormComponent): void {
  const { host, formEl } = component;
  const boundOnFormReset = onFormResetMap.get(host);

  if (!formEl) return;

  formEl.removeEventListener('reset', boundOnFormReset);
  onFormResetMap.delete(host);
  component.formEl = null;
  formComponentSet.delete(host);
}

/**
 * Helper for maintaining a form-associated's hidden input in sync with the component.
 *
 * Based on Ionic's approach: https://github.com/ionic-team/ionic-framework/blob/e4bf052794af9aac07f887013b9250d2a045eba3/core/src/utils/helpers.ts#L198
 */
export const HiddenFormElementSlot: FunctionalComponent<HiddenFormElementSlotProps> = ({
  component,
  component: { disabled, host, max, maxlength, min, minlength, name, pattern, readonly: readOnly, required, value },
  type,
}) => {
  const hiddenFormElementSlotName = 'hidden-form-element';
  const isInput = ['Checkbox', 'Input', 'Radio', 'Switch'].includes(component.constructor.name);

  let element = host.querySelector<HTMLInputElement | HTMLSelectElement>(
    `${isInput ? 'input' : 'select'}[slot="${hiddenFormElementSlotName}"]`
  );

  if (!element) {
    element = host.ownerDocument.createElement(isInput ? 'input' : 'select');

    const elementAttributes = {
      'aria-hidden': 'true',
      name,
      readOnly,
      style: 'opacity: 0; position: absolute; z-index: -1',
      tabIndex: -1,
    };

    if (isInput)
      Object.assign(elementAttributes, {
        max,
        maxlength,
        min,
        minlength,
        pattern,
        type,
      });

    setValidElementAttributes(element, elementAttributes);

    element.slot = hiddenFormElementSlotName;
    element.addEventListener('invalid', (event) => event.preventDefault());

    component.hiddenElement = element;

    host.appendChild(element);
  }

  setValidElementAttributes(element, { disabled, required });

  if (canBeCheckable(component)) (element as HTMLInputElement).checked = component.checked;
  if (canBeIndeterminate(component)) (element as HTMLInputElement).indeterminate = component.indeterminate;
  if (value !== undefined) element.value = value;

  return <slot name={hiddenFormElementSlotName} />;
};
